import {
    DigitacaoFormField,
    DigitacaoValidationFieldError,
    FormDataCLT,
    FormDataDigitacao,
} from '../../../../../models/SimulationBank/SimulationBankModels';
import { DigitacaoFormTab, getFieldGroup, getNestedValue, normalizeText, resolveSelectOptionValue, setNestedValue } from './digitacaoFormUtils';

// AI-1109 — reduz repreenchimento na digitação reaproveitando dado já capturado na
// jornada (autorização/margem/simulação), removendo campo não obrigatório e enviando
// valor fixo automaticamente, por financeira. Ver critérios de aceite do ticket.
//
// O "chave" de cada campo (ex.: "dados_cliente.pessoais.cpf") pode variar de posição
// entre financeiras, mas o nome do último segmento é estável (é o mesmo padrão que
// getFieldMask já usa pra decidir máscara). Por isso as regras são indexadas por
// FieldKind (o último segmento normalizado), não pelo "chave" completo.
export type FieldKind =
    | 'cpf'
    | 'nome_completo'
    | 'telefone'
    | 'email'
    | 'uf'
    | 'uf_naturalidade'
    | 'uf_emissao_rg'
    | 'cidade'
    | 'sexo'
    | 'nacionalidade'
    | 'data_nascimento'
    | 'data_emissao'
    | 'pep'
    | 'profissao'
    | 'ocupacao'
    | 'cnpj_empregador';

const FIELD_KIND_MATCHERS: [RegExp, FieldKind][] = [
    [/(^|_)cpf$/, 'cpf'],
    [/(^|_)nome(_completo)?$/, 'nome_completo'],
    [/(telefone|celular)$/, 'telefone'],
    [/(^|_)email$/, 'email'],
    [/(^|_)cidade$/, 'cidade'],
    [/(^|_)(sexo|genero)$/, 'sexo'],
    [/(^|_)nacionalidade$/, 'nacionalidade'],
    [/(^|_)data_nascimento$/, 'data_nascimento'],
    [/(^|_)data_emissao$/, 'data_emissao'],
    [/pep$/, 'pep'],
    [/profiss[aã]o$/, 'profissao'],
    [/ocupacao$/, 'ocupacao'],
    [/cnpj.*empregador$/, 'cnpj_empregador'],
];

// UF e cidade aparecem em mais de um contexto no mesmo formulário — endereço ("UF"),
// naturalidade ("UF de Naturalidade") e documento ("UF de Emissão do RG") —, e cada um é um
// dado diferente, então viram FieldKinds distintos. O último segmento sozinho não
// identifica o contexto (existem tanto "uf_naturalidade" quanto "naturalidade.uf"), por
// isso o caminho inteiro é lido; já o segmento é testado com fronteira nos dois lados pra
// pegar "uf_emissao" sem confundir com "estado_civil".
const UF_SEGMENT = /(^|_)(uf|estado)(_|$)/;
const CIVIL_SEGMENT = /civil/;
const NATURALIDADE_CONTEXT = /naturalidade|nascimento/;
const RG_CONTEXT = /emissao|emissor|(^|[._])rg([._]|$)/;

export const detectFieldKind = (chave: string): FieldKind | null => {
    const normalizedChave = normalizeText(chave);
    const segment = normalizeText(chave.split('.').pop() || '').replace(/[^a-z0-9_]/g, '');

    if (UF_SEGMENT.test(segment) && !CIVIL_SEGMENT.test(segment)) {
        if (NATURALIDADE_CONTEXT.test(normalizedChave)) return 'uf_naturalidade';
        if (RG_CONTEXT.test(normalizedChave)) return 'uf_emissao_rg';
        return 'uf';
    }

    const match = FIELD_KIND_MATCHERS.find(([pattern]) => pattern.test(segment));
    if (!match) return null;

    // "Cidade de Naturalidade" não é a cidade de residência capturada no Step1 e não tem
    // regra própria — fica sem classificação, em branco pro parceiro preencher.
    if (match[1] === 'cidade' && (NATURALIDADE_CONTEXT.test(normalizedChave) || RG_CONTEXT.test(normalizedChave))) {
        return null;
    }
    return match[1];
};

// Campos cuja obrigatoriedade depende do valor de outro campo do formulário — o schema
// devolve `required: false` e a regra condicional é aplicada aqui. Hoje só os dados do
// cônjuge (CPF e nome, Facta), obrigatórios apenas quando o estado civil é "Casado".
const CONJUGE_SEGMENT = /(^|_)(cpf|nome)_conjuge$/;

const lastSegment = (chave: string): string => normalizeText(chave.split('.').pop() || '');

export const isConjugeField = (chave: string): boolean => CONJUGE_SEGMENT.test(lastSegment(chave));

export const findEstadoCivilField = (fields: DigitacaoFormField[]): DigitacaoFormField | undefined =>
    fields.find((f) => CIVIL_SEGMENT.test(lastSegment(f.chave)));

// O `value` do select de estado civil varia por financeira ("CASADO", código interno...),
// então compara também pelo texto da opção. "Casado(a)" e afins entram pelo prefixo.
export const isEstadoCivilCasado = (field: DigitacaoFormField | undefined, value: unknown): boolean => {
    if (!field || value == null || value === '') return false;
    const option = field.option?.find((o) => String(o.value) === String(value));
    return [String(value), option?.innerText ?? ''].some((text) => normalizeText(text).startsWith('casad'));
};

// VCTEX passou a exigir o tipo do documento (RG ou CNH). O schema mantém um único bloco
// "dados_cliente.rg" pros dois — número, órgão, UF e data de emissão são os mesmos campos,
// e o integrador decide pelo `tipo_documento` como repassar à financeira. No formulário só
// mudam rótulo, placeholder e máscara do número conforme o tipo escolhido.
const DOCUMENTO_GROUP = 'dados_cliente.rg';
const CNH_LENGTH = 11;

const isDocumentoField = (chave: string, segment: string): boolean =>
    getFieldGroup(chave) === DOCUMENTO_GROUP && lastSegment(chave) === segment;

export const isDocumentoNumeroField = (chave: string): boolean => isDocumentoField(chave, 'numero');

export const findTipoDocumentoField = (fields: DigitacaoFormField[]): DigitacaoFormField | undefined =>
    fields.find((f) => isDocumentoField(f.chave, 'tipo_documento'));

export const isTipoDocumentoCnh = (value: unknown): boolean => String(value ?? '').toUpperCase() === 'CNH';

const CNH_FIELD_TEXT: Record<string, { label?: string; placeholder?: string }> = {
    numero: { label: 'Número da CNH', placeholder: `Digite os ${CNH_LENGTH} dígitos da CNH` },
    orgao_emissor: { placeholder: 'Ex: DETRAN' },
    uf_emissao: { label: 'UF de Emissão da CNH' },
};

export const getDocumentoFieldText = (
    field: DigitacaoFormField,
    tipoDocumento: unknown
): { label: string; placeholder?: string } => {
    const base = { label: field.label, placeholder: field.placeholder };
    if (!isTipoDocumentoCnh(tipoDocumento) || getFieldGroup(field.chave) !== DOCUMENTO_GROUP) return base;
    return { ...base, ...CNH_FIELD_TEXT[lastSegment(field.chave)] };
};

// RG: o pattern do schema só aceita letras e números (sem ponto/traço), então a máscara
// remove a pontuação em vez de formatar. CNH: só os dígitos do número de registro.
export const maskDocumentoNumero = (value: string, tipoDocumento: unknown): string =>
    isTipoDocumentoCnh(tipoDocumento)
        ? value.replace(/\D/g, '').slice(0, CNH_LENGTH)
        : value.replace(/[^0-9a-zA-Z]/g, '').toUpperCase();

export const validateDocumentoNumero = (value: string, tipoDocumento: unknown): string | true => {
    if (!value || !isTipoDocumentoCnh(tipoDocumento)) return true;
    return value.length === CNH_LENGTH ? true : `A CNH deve ter ${CNH_LENGTH} dígitos.`;
};

// Ao trocar RG ↔ CNH, número, órgão emissor e data de emissão do documento anterior não
// valem pro novo. A UF de emissão fica: vem pré-preenchida da jornada e costuma coincidir.
const DOCUMENTO_RESET_SEGMENTS = ['numero', 'orgao_emissor', 'data_emissao'];

export const getDocumentoFieldsToReset = (fields: DigitacaoFormField[]): DigitacaoFormField[] =>
    fields.filter((f) => DOCUMENTO_RESET_SEGMENTS.some((segment) => isDocumentoField(f.chave, segment)));

export interface DigitacaoReuseContext {
    formData: FormDataCLT | null;
}

export type DigitacaoFieldRule =
    // Campo não obrigatório pra financeira: some do form e não é enviado.
    | { mode: 'remove' }
    // Campo não solicitado ao parceiro: some do form, valor fixo sempre enviado. Para
    // campos `select`, informar `matchOptionLabel` em vez de `value` — o código do valor
    // aceito varia por financeira, então resolvemos pelo texto da opção retornada no
    // próprio schema (mesma lógica de match usada no autopreenchimento de CEP).
    | { mode: 'fixed'; value?: unknown; matchOptionLabel?: string }
    // Campo já capturado em etapa anterior da jornada: some do form, valor reaproveitado.
    | { mode: 'reuse'; source: (ctx: DigitacaoReuseContext) => unknown }
    // Campo já capturado na jornada, mas mantido visível e editável: entra pré-preenchido
    // com o valor da jornada, e o parceiro pode corrigi-lo. Usado quando o valor pode
    // divergir do exigido pela financeira (ex.: Nome — abreviação/acentuação) e travar o
    // campo geraria recusa na efetivação sem contorno.
    | { mode: 'prefill'; source: (ctx: DigitacaoReuseContext) => unknown };

const reuseFromFormData = (key: keyof FormDataCLT): DigitacaoFieldRule => ({
    mode: 'reuse',
    source: (ctx) => ctx.formData?.[key],
});

const prefillFromFormData = (key: keyof FormDataCLT): DigitacaoFieldRule => ({
    mode: 'prefill',
    source: (ctx) => ctx.formData?.[key],
});

// Chave resolvida por substring do nome retornado em `ofertaSelecionada.financeira`
// (nome de exibição vindo do back — ex.: "Soma- Bancarizadora UY3", "C6 Bank" —, não o
// canonical_id puro, daí o match por trecho em vez de igualdade/prefixo exato).
// SOMA_UY3/SOMA_CELCOIN precisam vir antes do fallback genérico 'SOMA' (find() pega o
// primeiro match) porque cada bancarizadora do grupo reaproveita um campo diferente
// (sexo pra UY3, data de nascimento pra Celcoin), além dos campos comuns às duas.
const FINANCEIRA_KEY_PATTERNS: [string, string][] = [
    ['uy3', 'SOMA_UY3'],
    ['celcoin', 'SOMA_CELCOIN'],
    ['soma', 'SOMA'],
    ['crefaz', 'CREFAZ'],
    ['c6', 'C6'],
    ['presen', 'PRESENCA'],
    ['facta', 'FACTA'],
    ['bmg', 'BMG'],
    ['vctex', 'VCTEX'],
];

export const resolveFinanceiraKey = (financeira: string): string | null => {
    const normalized = normalizeText(financeira);
    const match = FINANCEIRA_KEY_PATTERNS.find(([needle]) => normalized.includes(needle));
    return match ? match[1] : null;
};

// UF de Naturalidade e UF de Emissão do RG são, a rigor, dados distintos do estado de
// residência informado no Step1, mas coincidem na grande maioria dos casos — entram como
// 'prefill' (visíveis e editáveis) em todas as financeiras, pra poupar digitação sem
// impedir a correção quando divergirem. Só têm efeito onde o schema traz o campo.
const UF_ORIGEM_RULES: Partial<Record<FieldKind, DigitacaoFieldRule>> = {
    uf_naturalidade: prefillFromFormData('estado'),
    uf_emissao_rg: prefillFromFormData('estado'),
};

// Regras confirmadas nos critérios de aceite do AI-1109. Itens ainda pendentes de
// confirmação (ex.: Crefaz/Ocupação, referências mockadas da Crefaz) ficam de fora até
// serem validados — não implementar no escuro.
const FINANCEIRA_FIELD_RULES: Record<string, Partial<Record<FieldKind, DigitacaoFieldRule>>> = {
    CREFAZ: {
        email: { mode: 'remove' },
        cpf: reuseFromFormData('cpf'),
        nome_completo: prefillFromFormData('nome_completo'),
        uf: reuseFromFormData('estado'),
        ...UF_ORIGEM_RULES,
    },
    C6: {
        cpf: reuseFromFormData('cpf'),
        uf: prefillFromFormData('estado'),
        ...UF_ORIGEM_RULES,
    },
    PRESENCA: {
        nome_completo: prefillFromFormData('nome_completo'),
        cpf: reuseFromFormData('cpf'),
        uf: prefillFromFormData('estado'),
        ...UF_ORIGEM_RULES,
    },
    // Campos comuns às duas bancarizadoras do grupo Soma (mapeamento manual do Fabrin,
    // mais completo que o CA4 do ticket, que só cita nome/cpf/PEP/profissão).
    SOMA_UY3: {
        nome_completo: prefillFromFormData('nome_completo'),
        cpf: reuseFromFormData('cpf'),
        // AI-1109 (ajuste): nacionalidade estava com 'reuse' (campo oculto) e travando a
        // digitação quando o valor da jornada não batia com o exigido pela financeira,
        // sem forma de o parceiro corrigir. Trocado pra 'prefill' — mesmo tratamento
        // dado a nome_completo acima.
        nacionalidade: prefillFromFormData('nacionalidade'),
        sexo: reuseFromFormData('sexo'),
        telefone: reuseFromFormData('telefone'),
        uf: prefillFromFormData('estado'),
        ...UF_ORIGEM_RULES,
        pep: { mode: 'fixed', matchOptionLabel: 'Não' },
        profissao: { mode: 'fixed', matchOptionLabel: 'Assalariado' },
    },
    SOMA_CELCOIN: {
        nome_completo: prefillFromFormData('nome_completo'),
        cpf: reuseFromFormData('cpf'),
        nacionalidade: prefillFromFormData('nacionalidade'),
        data_nascimento: reuseFromFormData('data_nascimento'),
        telefone: reuseFromFormData('telefone'),
        uf: prefillFromFormData('estado'),
        ...UF_ORIGEM_RULES,
        pep: { mode: 'fixed', matchOptionLabel: 'Não' },
        profissao: { mode: 'fixed', matchOptionLabel: 'Assalariado' },
    },
    // Fallback caso o nome retornado pelo back não identifique a bancarizadora
    // específica (só "Soma") — cobre só o que é comum às duas.
    SOMA: {
        nome_completo: prefillFromFormData('nome_completo'),
        cpf: reuseFromFormData('cpf'),
        nacionalidade: prefillFromFormData('nacionalidade'),
        telefone: reuseFromFormData('telefone'),
        uf: prefillFromFormData('estado'),
        ...UF_ORIGEM_RULES,
        pep: { mode: 'fixed', matchOptionLabel: 'Não' },
        profissao: { mode: 'fixed', matchOptionLabel: 'Assalariado' },
    },
    VCTEX: {
        nacionalidade: prefillFromFormData('nacionalidade'),
        telefone: reuseFromFormData('telefone'),
        uf: prefillFromFormData('estado'),
        ...UF_ORIGEM_RULES,
    },
    BMG: {
        cpf: reuseFromFormData('cpf'),
        telefone: reuseFromFormData('telefone'),
        uf: reuseFromFormData('estado'),
        ...UF_ORIGEM_RULES,
        cidade: reuseFromFormData('cidade'),
    },
    // Mapeamento cruzado com a tabela de campos "etapa2-dados-pessoais" da Facta
    // (fornecida pelo PMO) contra o que o Step1 (FormDataCLT) já captura. Os demais
    // campos daquela etapa (estado_civil, RG completo, naturalidade, renda, patrimônio,
    // endereço detalhado, dados bancários, matrícula) não têm fonte na jornada e
    // continuam no formulário de digitação.
    FACTA: {
        cpf: reuseFromFormData('cpf'),
        nome_completo: prefillFromFormData('nome_completo'),
        sexo: reuseFromFormData('sexo'),
        data_nascimento: reuseFromFormData('data_nascimento'),
        nacionalidade: reuseFromFormData('nacionalidade'),
        telefone: reuseFromFormData('telefone'),
        uf: reuseFromFormData('estado'),
        ...UF_ORIGEM_RULES,
        cidade: reuseFromFormData('cidade'),
        cnpj_empregador: { mode: 'remove' },
    },
};

// Na VCTEX o bloco do RG também recebe CNH (ver DOCUMENTO_GROUP), então a aba deixa de
// citar o RG.
export const getDigitacaoTabLabel = (tab: DigitacaoFormTab, financeira: string): string =>
    tab.key === DOCUMENTO_GROUP && resolveFinanceiraKey(financeira) === 'VCTEX' ? 'Documento' : tab.label;

export const getDigitacaoFieldRule = (financeira: string, chave: string): DigitacaoFieldRule | null => {
    const financeiraKey = resolveFinanceiraKey(financeira);
    if (!financeiraKey) return null;
    const kind = detectFieldKind(chave);
    if (!kind) return null;
    return FINANCEIRA_FIELD_RULES[financeiraKey]?.[kind] ?? null;
};

const resolveFixedValue = (field: DigitacaoFormField, rule: Extract<DigitacaoFieldRule, { mode: 'fixed' }>): unknown => {
    if (rule.matchOptionLabel && field.option?.length) {
        const match = field.option.find((o) => normalizeText(o.innerText) === normalizeText(rule.matchOptionLabel!));
        if (match) return match.value;
    }
    return rule.value;
};

// AI-1109 (revertido temporariamente): esconder campos com regra 'remove'/'fixed'/'reuse'
// causava sumiço silencioso de campos obrigatórios do payload sempre que a fonte
// reaproveitada vinha vazia (ex.: sexo) ou o matchOptionLabel de um valor fixo não
// batia com o texto da opção retornada pelo schema (ex.: profissão) — o bloco inteiro
// desaparecia da UI sem nenhum erro visível, e o parceiro não tinha como corrigir.
// Até esses casos serem mapeados com certeza, todos os campos ficam visíveis; as regras
// de reaproveitamento/valor fixo continuam atuando como pré-preenchimento/injeção no
// payload (ver applyDigitacaoInjections), sem esconder nada do formulário.
export const filterVisibleDigitacaoFields = (fields: DigitacaoFormField[], _financeira: string): DigitacaoFormField[] =>
    fields;

// Aplica fixed/reuse por cima de um FormDataDigitacao já existente (ex.: dados digitados
// nas abas visíveis), sem perder o que já estava lá — usado no envio do payload pra
// garantir que o valor reaproveitado/fixo vá pra financeira mesmo se o campo nunca foi
// registrado no react-hook-form (por estar fora do form exibido).
export const applyDigitacaoInjections = (
    target: FormDataDigitacao | null,
    fields: DigitacaoFormField[],
    financeira: string,
    ctx: DigitacaoReuseContext
): FormDataDigitacao => {
    const result: FormDataDigitacao = target ? JSON.parse(JSON.stringify(target)) : {};
    fields.forEach((field) => {
        const rule = getDigitacaoFieldRule(financeira, field.chave);
        if (!rule || rule.mode === 'remove') return;

        // 'prefill' e 'reuse' só entram na primeira carga (campo ainda vazio): com todos
        // os campos visíveis (ver filterVisibleDigitacaoFields), o que o parceiro digitou
        // (ou manteve) já está em `target` e não pode ser sobrescrito de volta pelo valor
        // original da jornada. 'fixed' continua forçando o valor sempre — é regra de
        // negócio da financeira, não reaproveitamento de dado que o parceiro possa mudar.
        if (rule.mode === 'prefill' || rule.mode === 'reuse') {
            const existing = getNestedValue(result, field.chave);
            if (existing != null && existing !== '') return;
        }

        const value = rule.mode === 'fixed' ? resolveFixedValue(field, rule) : rule.source(ctx);
        if (value == null || value === '') return;

        // Em selects, o valor guardado na jornada (UF em sigla, cidade por extenso) quase
        // nunca é o mesmo `value` que a financeira espera — resolve pela opção do próprio
        // schema, senão o ReactSelect não acha a opção e o campo aparece vazio na tela.
        if (field.type === 'select') {
            const resolved = resolveSelectOptionValue(field, value);
            if (resolved == null) return;
            setNestedValue(result, field.chave, resolved);
            return;
        }

        setNestedValue(result, field.chave, value);
    });
    return result;
};

// SB-1953 — converte o `historico` do schema no valor que o formulário guarda. Devolve
// null quando o valor não serve, e aí o campo fica em branco pro parceiro preencher.
const resolveHistoricoValue = (field: DigitacaoFormField): unknown => {
    const raw = field.historico;
    if (raw == null || raw === '') return null;

    // Campo com catálogo (select/radio): o valor só entra se ainda existir entre as opções
    // atuais — o catálogo pode ter mudado desde a última digitação, e gravar algo fora do
    // domínio faz o select renderizar vazio e mesmo assim seguir no payload.
    if (field.option?.length) {
        const rawValue = String(raw);
        const exact = field.option.find((o) => String(o.value) === rawValue);
        if (exact) return exact.value;

        // Só se o match exato falhar: alguns catálogos usam o próprio texto acentuado como
        // value (ex.: cidade — "Águas de Santa Bárbara"), e o mesmo nome pode chegar numa
        // normalização Unicode diferente (NFC/NFD), o que derruba a comparação estrita num
        // valor que de fato existe. Comparar normalizado recupera esse caso sem inventar
        // opção: o que não casar nem assim continua "não selecionado".
        const normalized = normalizeText(rawValue);
        const loose = field.option.find((o) => normalizeText(String(o.value)) === normalized);
        return loose ? loose.value : null;
    }

    // Campos numéricos são guardados como número (é o que parseMoney produz na digitação
    // manual); o histórico pode chegar como string.
    if (field.type === 'number') {
        const parsed = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
        return Number.isFinite(parsed) ? parsed : null;
    }

    return raw;
};

// Preenche com a última digitação do cliente os campos que sobraram vazios. Roda DEPOIS de
// applyDigitacaoInjections de propósito: valor fixo é regra da financeira e o dado da
// jornada foi capturado agora (mais recente que o histórico), então o histórico entra só
// onde ninguém escreveu — o que inclui o que o parceiro já digitou.
// Não pula validação: `id_cadastro` continua novo a cada chamada, `status_validacao` nasce
// zerado e o POST /valida segue obrigatório para todos os blocos.
export const applyHistoricoPrefill = (
    target: FormDataDigitacao | null,
    fields: DigitacaoFormField[]
): FormDataDigitacao => {
    const result: FormDataDigitacao = target ? JSON.parse(JSON.stringify(target)) : {};
    fields.forEach((field) => {
        const existing = getNestedValue(result, field.chave);
        if (existing != null && existing !== '') return;

        const value = resolveHistoricoValue(field);
        if (value == null) return;

        setNestedValue(result, field.chave, value);
    });
    return result;
};

const CELULAR_PATTERN = /^\(\d{2}\)\s9\d{4}-\d{4}$/;

export const validateTelefoneCelular = (masked: string): string | true => {
    if (!masked) return true;
    return CELULAR_PATTERN.test(masked) ? true : 'Informe um celular válido, com DDD (ex.: (18) 99999-9999).';
};

const parseIsoDate = (value: string): Date | null => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
};

export const validateDataEmissao = (value: string, dataNascimento?: unknown): string | true => {
    if (!value) return true;
    const emissao = parseIsoDate(value);
    if (!emissao) return true;

    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    if (emissao.getTime() > hoje.getTime()) return 'Data de emissão não pode ser no futuro.';

    const nascimento = typeof dataNascimento === 'string' ? parseIsoDate(dataNascimento) : null;
    if (nascimento && emissao.getTime() <= nascimento.getTime()) {
        return 'Data de emissão deve ser posterior à data de nascimento.';
    }

    return true;
};

// Compara a cidade selecionada contra o retorno do ViaCEP para o CEP atual. `viaCepCidade`
// vem null quando o ViaCEP ainda não respondeu ou está fora do ar — nesse caso deixa
// passar, o integrador valida depois.
export const validateCidadeViaCep = (
    field: DigitacaoFormField,
    selectedValue: unknown,
    viaCepCidade: string | null
): string | true => {
    if (!viaCepCidade || selectedValue == null || selectedValue === '') return true;

    const selectedLabel = field.option?.find((o) => String(o.value) === String(selectedValue))?.innerText ?? String(selectedValue);
    return normalizeText(selectedLabel) === normalizeText(viaCepCidade)
        ? true
        : 'Cidade não corresponde ao CEP informado.';
};

const MENSAGEM_CAMPO_PATTERN = /([a-z0-9_]+(?:\.[a-z0-9_]+)+):\s*([^|]+)/gi;

export const parseFieldErrorsFromMensagem = (mensagem: string): DigitacaoValidationFieldError[] =>
    [...mensagem.matchAll(MENSAGEM_CAMPO_PATTERN)].map((m) => ({
        field: m[1].trim(),
        message: m[2].trim(),
        type: 'server',
    }));

const MENSAGEM_FIELD_KIND_MATCHERS: [RegExp, FieldKind][] = [
    [/telefone|celular/i, 'telefone'],
    [/cidade|cep/i, 'cidade'],
    [/emiss/i, 'data_emissao'],
];

export const detectFieldKindFromMensagem = (mensagem: string): FieldKind | null => {
    const match = MENSAGEM_FIELD_KIND_MATCHERS.find(([pattern]) => pattern.test(mensagem));
    return match ? match[1] : null;
};

// BMG e FACTA reaproveitam a cidade da própria jornada em vez de deixá-la como campo
// normal ligado ao CEP (ver FINANCEIRA_FIELD_RULES) — nesses dois casos o campo não foi
// pensado pra ser comparado com o ViaCEP do CEP digitado aqui, então a validação de
// cidade×CEP não se aplica. Reaproveita getDigitacaoFieldRule em vez de repetir a lista de
// financeiras, pra não ter duas fontes da mesma regra.
export const isCidadeReaproveitadaDaJornada = (financeira: string, chave: string): boolean =>
    getDigitacaoFieldRule(financeira, chave)?.mode === 'reuse';
